import { Component, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalComponent, ModalButton } from '../../../common-components/modal/modal.component';
import { AdminSubmission, CardsAdminService, SubmissionApproveFields } from '../../../services/admin/cards-admin.service';

@Component({
  selector: 'app-cards-submissions-tab',
  imports: [TranslocoModule, ModalComponent, ReactiveFormsModule, DatePipe],
  templateUrl: './cards-submissions-tab.component.html',
  styleUrl: './cards-submissions-tab.component.scss'
})
export class CardsSubmissionsTabComponent {
  statusFilter = signal<'pending' | 'all'>('pending');
  submissions = rxResource({
    stream: () => this.cardsAdmin.getSubmissions$(this.statusFilter()),
    defaultValue: []
  });

  showReviewModal = signal(false);
  selectedSubmission = signal<AdminSubmission | null>(null);

  nameEnControl = new FormControl<string>('', { nonNullable: true });
  nameUkControl = new FormControl<string>('', { nonNullable: true });
  quoteEnControl = new FormControl<string>('', { nonNullable: true });
  quoteUkControl = new FormControl<string>('', { nonNullable: true });
  reasonControl = new FormControl<string>('', { nonNullable: true });

  // Double-click guard (ModalButton has no disabled slot, so this is the mechanism - mirrors
  // the mobile side's single-flight guards for the same constraint).
  private submitting = signal(false);
  // Belt 1: best-effort true cancellation - HttpClient aborts the request on unsubscribe.
  private verdictSub?: Subscription;
  // Belt 2: even if the in-flight request already left the wire, its response is stamped with
  // the review session it belongs to - a stale response's callback checks this before touching
  // modal state, snackbars, or reload, so a late verdict for a since-closed/reopened review can
  // never clobber whatever review is open (or not) by the time it lands.
  private reviewSeq = 0;

  reviewModalButtons: ModalButton[] = [
    { type: 'secondary', text: this.transloco.translate('Cancel'), onClick: () => this.closeReview() },
    { type: 'error', text: this.transloco.translate('cardsAdminReject'), onClick: () => this.reject() },
    { type: 'primary', text: this.transloco.translate('cardsAdminApprove'), onClick: () => this.approve() }
  ];

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {}

  setStatusFilter(status: 'pending' | 'all'): void {
    this.statusFilter.set(status);
  }

  statusClass(submission: AdminSubmission): string {
    if (submission.status === 'rejected') {
      return 'color-red';
    }
    if (submission.status === 'approved' || submission.status === 'published') {
      return 'color-green';
    }
    return '';
  }

  openReview(submission: AdminSubmission): void {
    this.reviewSeq++;
    this.selectedSubmission.set(submission);
    // Prefill + mark pristine so `.dirty` afterward reflects only what the admin actually
    // edits in the modal - that's how DIRTY fields get picked for the approve request.
    this.nameEnControl.setValue(submission.name.en);
    this.nameEnControl.markAsPristine();
    this.nameUkControl.setValue(submission.name.uk);
    this.nameUkControl.markAsPristine();
    this.quoteEnControl.setValue(submission.quote.en);
    this.quoteEnControl.markAsPristine();
    this.quoteUkControl.setValue(submission.quote.uk);
    this.quoteUkControl.markAsPristine();
    this.reasonControl.setValue('');
    this.reasonControl.markAsPristine();
    this.showReviewModal.set(true);
  }

  closeReview(): void {
    this.reviewSeq++;
    // Best-effort true cancellation - HttpClient tears down the underlying XHR/fetch on
    // unsubscribe, so a Cancel click during an in-flight approve/reject also aborts it on the
    // wire, not just locally.
    this.verdictSub?.unsubscribe();
    this.submitting.set(false);
    this.showReviewModal.set(false);
  }

  approve(): void {
    const submission = this.selectedSubmission();
    if (!submission || this.submitting()) {
      return;
    }
    const fields: SubmissionApproveFields = {};
    if (this.nameEnControl.dirty) {
      fields.nameEn = this.nameEnControl.value.trim();
    }
    if (this.nameUkControl.dirty) {
      fields.nameUk = this.nameUkControl.value.trim();
    }
    if (this.quoteEnControl.dirty) {
      fields.quoteEn = this.quoteEnControl.value.trim();
    }
    if (this.quoteUkControl.dirty) {
      fields.quoteUk = this.quoteUkControl.value.trim();
    }
    const seq = this.reviewSeq;
    this.submitting.set(true);
    this.verdictSub?.unsubscribe();
    this.verdictSub = this.cardsAdmin.approveSubmission$(submission.id, fields).subscribe({
      next: () => {
        // The review this response belongs to may have since been closed/replaced (Cancel,
        // or - defense in depth - a reopen) - a stale response must never force-close a
        // DIFFERENT review, misattribute its success toast, or apply a "cancelled" verdict.
        if (seq !== this.reviewSeq) {
          return;
        }
        this.submitting.set(false);
        this.showReviewModal.set(false);
        this.snackBar.open(this.transloco.translate('cardsAdminApproved'), '', { duration: 3000 });
        this.submissions.reload();
      },
      error: () => {
        if (seq !== this.reviewSeq) {
          return;
        }
        this.submitting.set(false);
      }
    });
  }

  reject(): void {
    const submission = this.selectedSubmission();
    const reason = this.reasonControl.value.trim();
    if (!submission || !reason || this.submitting()) {
      return;
    }
    const seq = this.reviewSeq;
    this.submitting.set(true);
    this.verdictSub?.unsubscribe();
    this.verdictSub = this.cardsAdmin.rejectSubmission$(submission.id, reason).subscribe({
      next: () => {
        if (seq !== this.reviewSeq) {
          return;
        }
        this.submitting.set(false);
        this.showReviewModal.set(false);
        this.snackBar.open(this.transloco.translate('cardsAdminRejectedMsg'), '', { duration: 3000 });
        this.submissions.reload();
      },
      error: () => {
        if (seq !== this.reviewSeq) {
          return;
        }
        this.submitting.set(false);
      }
    });
  }
}
